import React from 'react';
import { Button } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { IconTextContent } from './IconTextContent';
import { withTooltip } from './tooltipHelpers';

export interface IconOverTextButtonProps {
  /** The icon to display above the text */
  icon: React.ReactNode;
  /** The text to display below the icon */
  text: string;
  /** The tooltip to show on hover */
  tooltip?: string;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether the button is in a sunken/active state */
  sunken?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether to show hover background effect */
  showHoverEffect?: boolean;
  /** Additional sx styles to apply */
  sx?: SxProps<Theme>;
  /** Wrap in span for disabled tooltip support */
  wrapInSpan?: boolean;
  /** Color variant for the button (e.g., 'error' for delete buttons) */
  color?: 'error' | 'primary' | 'secondary' | 'success' | 'warning' | 'info';
  /** Whether to show loading spinner instead of icon */
  loading?: boolean;
}

/**
 * A button component that displays an icon above text with optional sunken/active state
 * Used for toolbar actions like "My Projects", "Columns", "Filter", etc.
 */
export const IconOverTextButton: React.FC<IconOverTextButtonProps> = ({
  icon,
  text,
  tooltip,
  onClick,
  sunken = false,
  disabled = false,
  showHoverEffect = true,
  sx,
  wrapInSpan = false,
  color,
  loading = false,
}) => {
  // Get the appropriate color value
  const getColorValue = (theme: Theme) => {
    if (color === 'error') return theme.palette.error.main;
    if (color === 'primary') return theme.palette.primary.main;
    if (color === 'secondary') return theme.palette.secondary.main;
    if (color === 'success') return theme.palette.success.main;
    if (color === 'warning') return theme.palette.warning.main;
    if (color === 'info') return theme.palette.info.main;
    return 'rgba(0, 0, 0, 0.87)';
  };

  const button = (
    <Button
      variant='text'
      onClick={onClick}
      disabled={disabled || loading}
      sx={{
        textTransform: 'none',
        px: 1,
        py: 1,
        minWidth: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        color: theme => (color ? `${getColorValue(theme)} !important` : 'rgba(0, 0, 0, 0.87) !important'),
        // Sunken state styles
        ...(sunken && {
          backgroundColor: '#e0e0e0',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            backgroundColor: '#d5d5d5',
            color: (theme: Theme) => (color ? `${getColorValue(theme)} !important` : 'rgba(0, 0, 0, 0.87) !important'),
          },
        }),
        // Normal hover effect
        ...(!sunken &&
          showHoverEffect && {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: (theme: Theme) => (color ? `${getColorValue(theme)} !important` : 'rgba(0, 0, 0, 0.87) !important'),
            },
          }),
        // No hover effect
        ...(!sunken &&
          !showHoverEffect && {
            '&:hover': {
              backgroundColor: 'transparent',
              color: (theme: Theme) => (color ? `${getColorValue(theme)} !important` : 'rgba(0, 0, 0, 0.87) !important'),
            },
          }),
        // Disabled state
        '&.Mui-disabled': {
          color: 'rgba(0, 0, 0, 0.38) !important',
        },
        // Merge custom styles
        ...sx,
      }}
    >
      <IconTextContent icon={icon} text={text} loading={loading} />
    </Button>
  );

  return withTooltip(button, tooltip, wrapInSpan);
};

export default IconOverTextButton;
